"use client";

import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useRouter } from "next/navigation";
import { INTL_LOCALE, LOCALE_COOKIE, type Locale } from "./config";
import { translate, type MessageKey, type Params } from "./translate";

interface I18nContextType {
  locale: Locale;
  /** Typed lookup: only keys that exist in the Bahasa dictionary compile. */
  t: (key: MessageKey, params?: Params) => string;
  /** Loosely typed lookup for keys built at runtime, e.g. tx(`status.booking.${status}`). */
  tx: (key: string, params?: Params) => string;
  setLocale: (locale: Locale) => void;
  formatDate: (value: string | number | Date | null | undefined, options?: Intl.DateTimeFormatOptions) => string;
  formatDateTime: (value: string | number | Date | null | undefined, options?: Intl.DateTimeFormatOptions) => string;
  formatTime: (value: string | number | Date | null | undefined, options?: Intl.DateTimeFormatOptions) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") return null;
  // SQLite datetime('now') stores "YYYY-MM-DD HH:MM:SS" in UTC with no zone marker.
  const raw = typeof value === "string" && SQLITE_UTC.test(value) ? `${value.replace(" ", "T")}Z` : value;
  const d = raw instanceof Date ? raw : new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

const SQLITE_UTC = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/** A bare calendar date ("2026-10-14") has no time zone: it parses as UTC midnight, so it must be printed in UTC too. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export function I18nProvider({ locale, children }: { locale: Locale; children: React.ReactNode }) {
  const router = useRouter();

  const value = useMemo<I18nContextType>(() => {
    const tag = INTL_LOCALE[locale];
    const tx = (key: string, params?: Params) => translate(locale, key, params);
    // Full month name: "1 Oktober 2026" (id, d MMMM yyyy) / "October 1, 2026" (en).
    const dateDefaults: Intl.DateTimeFormatOptions = { day: "numeric", month: "long", year: "numeric" };
    const fmtDate = (v: string | number | Date | null | undefined, o: Intl.DateTimeFormatOptions) => {
      const d = toDate(v);
      if (!d) return "";
      const opts = typeof v === "string" && DATE_ONLY.test(v) && !o.timeZone ? { ...o, timeZone: "UTC" } : o;
      return new Intl.DateTimeFormat(tag, opts).format(d);
    };
    return {
      locale,
      t: tx,
      tx,
      setLocale: () => undefined,
      formatDate: (v, o = dateDefaults) => fmtDate(v, o),
      formatDateTime: (v, o = { ...dateDefaults, hour: "2-digit", minute: "2-digit" }) => fmtDate(v, o),
      formatTime: (v, o = { hour: "2-digit", minute: "2-digit" }) => fmtDate(v, o),
      formatNumber: (n, o) => new Intl.NumberFormat(tag, o).format(n),
    };
  }, [locale]);

  const setLocale = useCallback(
    (next: Locale) => {
      document.cookie = `${LOCALE_COOKIE}=${next}; path=/; max-age=31536000; SameSite=Lax`;
      router.refresh();
    },
    [router]
  );

  const ctx = useMemo(() => ({ ...value, setLocale }), [value, setLocale]);
  return <I18nContext.Provider value={ctx}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextType {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within <I18nProvider>");
  return ctx;
}

/** Shorthand for components that only need `t`. */
export function useT() {
  const { t, tx } = useI18n();
  return { t, tx };
}
